import { Flex } from '@chakra-ui/layout';
import { type PropsWithChildren, useEffect, useMemo, useRef } from 'react';
import Rupt from 'rupt';
import { useQuery } from 'urql';

import { fullLogout, isUserTrialExpired, useAuthStore } from '@revelio/auth';
import { Loading } from '@revelio/core';
import {
  type GetUserDetailsQuery,
  USER_DETAILS_QUERY,
} from '@revelio/data-access';

import { environment } from '../../environments/environment';
import { useDashMetaCsrf } from './useDashMetaCsrf';
import { useUserTracking } from './useUserTracking';
import { useWhoAmI } from './useWhoAmI';

const AuthGateContent = ({ children }: PropsWithChildren) => {
  useUserTracking();

  return children;
};

export const AuthGate = ({ children }: PropsWithChildren) => {
  const { ory, dashMetaCsrfToken, isAuthenticated, user } = useAuthStore();
  const setUser = useAuthStore((state) => state.setUser);
  const ruptInitialized = useRef(false);

  const { isLoading: oryIsLoading } = useWhoAmI(ory);
  const { isLoading: dashMetaCsrfIsLoading } = useDashMetaCsrf();

  const isAuthenticating = oryIsLoading || dashMetaCsrfIsLoading;
  const userId = ory?.id;
  const email = user?.email ?? ory?.email;
  const isProd = environment.production;

  const [{ fetching, data }] = useQuery<GetUserDetailsQuery>({
    query: USER_DETAILS_QUERY,
    variables: { ids: userId ? [userId] : [] },
    pause: !userId,
  });

  const authorization = useMemo(() => {
    if (!data) {
      return { isAuthorized: false, user: undefined, error: undefined };
    }

    try {
      const resolvedUser = data.users?.[0];
      if (!resolvedUser) throw new Error('USER_NOT_FOUND');
      if (!resolvedUser.active) throw new Error('USER_NOT_ACTIVE');
      if (isUserTrialExpired(resolvedUser)) {
        throw new Error('USER_TRIAL_EXPIRED');
      }

      return { isAuthorized: true, user: resolvedUser, error: undefined };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : `${err}`;
      return { isAuthorized: false, user: undefined, error: errorMessage };
    }
  }, [data]);

  useEffect(() => {
    if (!authorization.error) {
      return;
    }

    console.error(authorization.error);
    fullLogout();
  }, [authorization.error]);

  useEffect(() => {
    if (authorization.isAuthorized && authorization.user) {
      setUser(authorization.user);
    }
  }, [authorization.isAuthorized, authorization.user, setUser]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: migrated from develop
  useEffect(() => {
    const initializeRupt = async () => {
      if (isProd && userId && email && !ruptInitialized.current) {
        try {
          await Rupt.attach({
            client_id: environment.RUPT_CLIENT_ID,
            account: userId,
            email: email,
          });
          ruptInitialized.current = true;
        } catch (error) {
          console.error('Failed to initialize Rupt:', error);
        }
      }
    };
    void initializeRupt();
  }, [userId, email, isProd]);

  if (
    isAuthenticating ||
    !isAuthenticated ||
    !dashMetaCsrfToken ||
    !userId ||
    fetching ||
    !authorization.isAuthorized
  ) {
    return (
      <Flex height="100vh">
        <Loading />
      </Flex>
    );
  }

  return <AuthGateContent>{children}</AuthGateContent>;
};
